import React, { useState } from 'react';
import { Settings2, RotateCcw, X } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useThemeConfig } from '../../hooks/use-theme-config';
import { type LayoutConfig } from '../../hooks/use-layout-config';
import { ThemeControls } from './theme-controls';
import { LayoutControls } from './layout-controls';

interface CustomizerPanelProps {
  layoutConfig: LayoutConfig;
  onLayoutChange: (config: LayoutConfig) => void;
}

export const CustomizerPanel: React.FC<CustomizerPanelProps> = ({
  layoutConfig,
  onLayoutChange,
}) => {
  const [open, setOpen] = useState(false);
  const { themeConfig, setThemeConfig, resetTheme } = useThemeConfig();

  const handleReset = () => {
    resetTheme();
    onLayoutChange({ sidebarVariant: 'inset', sidebarCollapsible: 'offcanvas', sidebarSide: 'left' });
  };

  return (
    <>
      {/* ── Floating trigger button ───────────────────────────────────── */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-foreground text-background hover:bg-foreground/90 shadow-2xl [&_svg]:size-5"
              onClick={() => setOpen(true)}
              aria-label="Open customizer"
            >
              <Settings2 />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Customize</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* ── Customizer Sheet ─────────────────────────────────────────── */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-[380px] sm:w-[380px] p-0 flex flex-col gap-0 [&>button]:hidden dark:bg-popover"
        >
          {/* Sticky header */}
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 sticky top-0 bg-background dark:bg-popover z-10">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Customizer</span>
            </div>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipProvider>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleReset}
                      aria-label="Reset all settings"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Reset to defaults</TooltipContent>
                </TooltipProvider>
              </Tooltip>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setOpen(false)}
                aria-label="Close customizer"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="theme" className="flex flex-1 flex-col min-h-0">
            <div className="px-4 pt-3 pb-3 shrink-0">
              <TabsList className="grid w-full grid-cols-2 dark:bg-muted/40">
                <TabsTrigger
                  value="theme"
                  className="gap-1.5 text-sm dark:data-[state=active]:bg-foreground dark:data-[state=active]:text-background dark:data-[state=active]:shadow-none"
                >
                  <span>🎨</span>
                  Theme
                </TabsTrigger>
                <TabsTrigger
                  value="layout"
                  className="gap-1.5 text-sm dark:data-[state=active]:bg-foreground dark:data-[state=active]:text-background dark:data-[state=active]:shadow-none"
                >
                  <span>⊞</span>
                  Layout
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <TabsContent value="theme" className="mt-0 p-4 pb-8 space-y-0">
                <ThemeControls
                  config={themeConfig}
                  onChange={(update) => setThemeConfig(update)}
                />
              </TabsContent>
              <TabsContent value="layout" className="mt-0 p-4 pb-8 space-y-0">
                <LayoutControls config={layoutConfig} onChange={onLayoutChange} />
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </SheetContent>
      </Sheet>
    </>
  );
};
